import React, { useCallback, useEffect, useState } from 'react';
import { accessAddressesService, clientsService, type EnderecoDeAcesso } from '../../services/api';
import { campo, mensagemDoErroDeCarga } from '../../design';
import { TONE_TEXT } from '../../utils/statusColors';
import { fmtPhone } from '../../utils/formatters';
import { normalizarHost, sugestaoPeloDominioSip } from '../../utils/enderecoDeAcesso';

/**
 * ENDEREÇOS DE ACESSO do cliente (C2 + D1, Heitor 21/10/2026; backend E1 #514). O buraco que o Heitor apontou: a aba
 * Infraestrutura mostrava o Domínio SIP (agger.ipcom.com.br) e não o endereço do APP onde o cliente fica
 * (com8dn.ipcom.app.br, ou o compartilhado sbc.ipcom.app.br). Esta é a lista desses endereços; o DDR escolhe um deles.
 *
 * - Gravação imediata pela rota própria (PUT /clients/:id/access-addresses, que SUBSTITUI o conjunto), fora do Salvar
 *   da ficha, como a retenção S3: vincular não pode depender de salvar outro campo.
 * - Cadastrar endereço novo é SÓ aqui (F1): o DDR não cria endereço.
 * - D1: com Domínio SIP, sugere o endereço pelo prefixo; nada é gravado sem o clique.
 * - As tabelas nascem VAZIAS (o backfill espera a palavra do Heitor): o vazio diz que não há endereço, sem fingir erro.
 */
interface Props {
  /** Sem id (cliente novo), não há onde vincular: a seção diz para salvar primeiro. */
  clientId?: number;
  /** Os endereços do cliente como vieram do GET /clients/:id. Ausente = o servidor não mandou (não é "nenhum"). */
  vinculadosIniciais?: EnderecoDeAcesso[];
  dominioSip?: string | null;
  /** Os DDRs DESTE cliente, para dizer quantos usam cada endereço. */
  ddrsDoCliente: Array<{ id: number; accessAddressId?: number | null }>;
  podeEditar: boolean;
  /** Depois de gravar: o App relê a lista de clientes, de onde o modal do DDR tira os endereços. */
  onMudou?: () => void;
}

type Recusa = { texto: string; ddrs?: Array<{ id: number; phoneNumber: string }> };

const recusaDe = (e: any): Recusa => {
  const d = e?.response?.data;
  if (d?.code === 'ENDERECO_EM_USO') return { texto: d.message, ddrs: Array.isArray(d.ddrsQueImpedem) ? d.ddrsQueImpedem : [] };
  return { texto: d?.message && typeof d.message === 'string' ? d.message : mensagemDoErroDeCarga(e) };
};

export default function EnderecosDeAcesso({ clientId, vinculadosIniciais, dominioSip, ddrsDoCliente, podeEditar, onMudou }: Props) {
  const [vinculados, setVinculados] = useState<EnderecoDeAcesso[] | undefined>(vinculadosIniciais);
  const [catalogo, setCatalogo] = useState<EnderecoDeAcesso[] | null>(null);
  const [erroDoCatalogo, setErroDoCatalogo] = useState<string | null>(null);
  const [gravando, setGravando] = useState(false);
  const [recusa, setRecusa] = useState<Recusa | null>(null);
  const [novo, setNovo] = useState<string | null>(null);

  useEffect(() => { setVinculados(vinculadosIniciais); }, [vinculadosIniciais]);

  const lerCatalogo = useCallback(async () => {
    setErroDoCatalogo(null);
    try {
      const lista = await accessAddressesService.listar();
      setCatalogo(Array.isArray(lista) ? lista : []);
    } catch (e) {
      setErroDoCatalogo(mensagemDoErroDeCarga(e));
    }
  }, []);
  useEffect(() => { if (clientId) lerCatalogo(); }, [clientId, lerCatalogo]);

  if (!clientId) {
    return <p className='text-xs text-slate-500' data-enderecos='cliente-novo'>Salve o cliente para vincular os endereços de acesso.</p>;
  }
  if (!Array.isArray(vinculados)) {
    return <p role='alert' className={`text-xs ${TONE_TEXT.warning}`} data-enderecos='nao-lidos'>Não consegui ler os endereços de acesso deste cliente: feche e abra a ficha de novo.</p>;
  }

  const ids = vinculados.map((v) => v.id);
  const usoNoCliente = (id: number) => ddrsDoCliente.filter((d) => d.accessAddressId === id).length;
  const doCatalogo = (id: number) => catalogo?.find((c) => c.id === id);

  const gravar = async (novosIds: number[]) => {
    setGravando(true);
    setRecusa(null);
    try {
      const r = await clientsService.definirEnderecosDeAcesso(clientId, novosIds);
      setVinculados(Array.isArray(r) ? r : []);
      setNovo(null);
      onMudou?.();
      lerCatalogo();
      return true;
    } catch (e) {
      setRecusa(recusaDe(e));
      return false;
    } finally {
      setGravando(false);
    }
  };

  /** Cria (ou acha, quando já existe) e vincula. ENDERECO_JA_EXISTE devolve o id do que já está lá: usa ele. */
  const cadastrarEVincular = async (hostDigitado: string) => {
    const host = normalizarHost(hostDigitado);
    const existente = catalogo?.find((c) => c.host === host);
    if (existente) return gravar([...ids, existente.id]);
    setGravando(true);
    setRecusa(null);
    let id: number | null = null;
    try {
      id = (await accessAddressesService.criar(host)).id;
    } catch (e: any) {
      const d = e?.response?.data;
      if (d?.code === 'ENDERECO_JA_EXISTE' && typeof d.id === 'number') id = d.id;
      else { setRecusa(recusaDe(e)); setGravando(false); return false; }
    }
    setGravando(false);
    return gravar([...ids, id as number]);
  };

  const sugerido = sugestaoPeloDominioSip(dominioSip);
  const mostrarSugestao = podeEditar && !!sugerido && !vinculados.some((v) => v.host === sugerido);
  const paraVincular = (catalogo || []).filter((c) => !ids.includes(c.id)).sort((a, b) => a.host.localeCompare(b.host));

  return (
    <div className='space-y-2' data-enderecos='lista'>
      {vinculados.length === 0 ? (
        <p className='text-xs text-slate-400' data-enderecos-vazio>
          Nenhum endereço vinculado a este cliente.
          {catalogo && catalogo.length === 0 && ' Ainda não há endereços cadastrados no HUB.'}
        </p>
      ) : (
        <ul className='space-y-1.5'>
          {vinculados.map((v) => {
            const n = usoNoCliente(v.id);
            const outros = Math.max(0, (doCatalogo(v.id)?.clientes ?? 1) - 1);
            return (
              <li key={v.id} className='flex items-center justify-between gap-3 px-3 py-2 rounded-lg border border-central-borda bg-central-elevado' data-endereco-vinculado={v.host}>
                <div className='min-w-0'>
                  <span className='block font-mono text-sm text-central-texto truncate' title={v.host}>{v.host}</span>
                  <span className='block text-[11px] text-slate-500'>
                    {n === 0 ? 'nenhum DDR deste cliente' : `${n} DDR${n === 1 ? '' : 's'} deste cliente`}
                    {outros > 0 && ` · compartilhado com mais ${outros} cliente${outros === 1 ? '' : 's'}`}
                  </span>
                </div>
                {podeEditar && (
                  <button type='button' disabled={gravando} onClick={() => gravar(ids.filter((x) => x !== v.id))} className='shrink-0 text-xs text-slate-400 hover:text-white underline disabled:opacity-50'>
                    desvincular
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {recusa && (
        <div role='alert' className={`text-xs ${TONE_TEXT.danger}`} data-enderecos-recusa>
          <p>{recusa.texto}</p>
          {recusa.ddrs && recusa.ddrs.length > 0 && (
            <p className='mt-1 font-mono text-slate-300'>
              {recusa.ddrs.slice(0, 12).map((d) => fmtPhone(d.phoneNumber)).join(' · ')}{recusa.ddrs.length > 12 ? ` · +${recusa.ddrs.length - 12}` : ''}
            </p>
          )}
        </div>
      )}

      {mostrarSugestao && (
        <div className='flex items-center justify-between gap-3 px-3 py-2 rounded-lg border border-dashed border-primary text-xs text-slate-300' data-enderecos-sugestao={sugerido}>
          <span>Sugestão pelo Domínio SIP: <span className='font-mono text-central-texto'>{sugerido}</span></span>
          <button type='button' disabled={gravando || catalogo === null} onClick={() => cadastrarEVincular(sugerido as string)} className='shrink-0 text-primary hover:text-white underline disabled:opacity-50'>Vincular</button>
        </div>
      )}

      {podeEditar && (
        erroDoCatalogo ? (
          <p role='alert' className={`text-xs ${TONE_TEXT.warning}`} data-enderecos-catalogo='erro'>
            Não consegui ler a lista de endereços ({erroDoCatalogo}): por ora não dá para vincular.
            <button type='button' onClick={lerCatalogo} className='ml-2 underline'>Tentar de novo</button>
          </p>
        ) : novo !== null ? (
          <div className='flex items-center gap-2' data-enderecos-novo>
            <input autoFocus aria-label='Novo endereço de acesso' value={novo} onChange={(e) => setNovo(e.target.value)} placeholder='ex.: cliente.ipcom.app.br' className={`flex-1 px-3 py-2 rounded-lg text-sm font-mono ${campo}`} />
            <button type='button' disabled={gravando || !normalizarHost(novo)} onClick={() => cadastrarEVincular(novo)} className='px-3 py-2 rounded-lg text-sm font-medium bg-primary text-white hover:bg-primary-strong disabled:opacity-50'>Cadastrar e vincular</button>
            <button type='button' onClick={() => setNovo(null)} className='text-xs text-slate-400 hover:text-white underline'>cancelar</button>
          </div>
        ) : (
          <select
            aria-label='Vincular endereço'
            value=''
            disabled={gravando || catalogo === null}
            onChange={(e) => { const v = e.target.value; if (v === '__novo__') setNovo(''); else if (v) gravar([...ids, Number(v)]); }}
            className={`w-full px-3 py-2 rounded-lg text-sm ${campo}`}
            data-enderecos-vincular
          >
            <option value=''>{catalogo === null ? 'Lendo os endereços…' : 'Vincular endereço…'}</option>
            {paraVincular.map((c) => (
              <option key={c.id} value={String(c.id)}>{c.host}{(c.clientes ?? 0) > 0 ? ` (${c.clientes} cliente${c.clientes === 1 ? '' : 's'})` : ''}</option>
            ))}
            <option value='__novo__'>+ Novo endereço…</option>
          </select>
        )
      )}
    </div>
  );
}
